// src/store/modules/department.ts
import { gql } from 'apollo-boost';
import { client } from '@/lib/api';
import { preventError } from '@/utils/';
import { GetDepartmentsQuery, GetDepartmentsQueryVariables, PagerInput, SearchInput } from '@/generated/graphql';

interface GetDepartmentsPayload {
  institutionId?: string | string[];
  departmentId?: string[];
  pager?: PagerInput;
  search?: SearchInput | SearchInput[];
  freezeCache?: boolean;
}

export default {
  namespaced: true,

  // ---------------- STATE ----------------
  state: () => ({
    departments: [],
    departmentsPageInfo: {},
    refreshDepartments: false,
  }),

  // ---------------- MUTATIONS ----------------
  mutations: {
    SET_DEPARTMENTS_LIST(state, val) {
      state.departments = val;
    },
    SET_DEPARTMENTS_PAGEINFO(state, val) {
      state.departmentsPageInfo = val;
    },
    SET_REFRESH_DEPARTMENTS(state, val) {
      state.refreshDepartments = val;
    },
  },

  // ---------------- GETTERS ----------------
  getters: {
    departmentsList(state) {
      return state.departments.map((item) => {
        return {
          id: item.id,
          name: item.name,
          institutionName: item.institution.name,
          adminName: item.users
            .filter((user) => user.roles.find((role) => role.role === 'department_admin'))
            .map((user) => user.name)
            .join(','),
          dateAdded: (() => {
            if (!item.createdAt) return { name: '-', helped: '-', date: null };
            const date = new Date(item.createdAt);
            return {
              name: `${date.toLocaleString('default', { month: 'short' })} ${date.getDate()}, ${date.getFullYear()}`,
              helped: date.toLocaleString('en-US', {
                hour: 'numeric',
                minute: 'numeric',
                hour12: true,
              }),
              date,
            };
          })(),
        };
      });
    },
  },

  // ---------------- ACTIONS ----------------
  actions: {
    // === dispatch('departments/GET_DEPARTMENTS') ===
    async GET_DEPARTMENTS({ commit, rootGetters }, payload: GetDepartmentsPayload = {}) {
      const institutionId = payload.institutionId || rootGetters.activeInstitutionId;
      const input = {
        institutionId: Array.isArray(institutionId) ? institutionId : [institutionId],
        id: payload.departmentId || [],
        pager: payload.pager,
        search: Array.isArray(payload.search) ? payload.search : payload.search ? [payload.search] : undefined,
      };

      try {
        const response = await client.query<GetDepartmentsQuery, GetDepartmentsQueryVariables>({
          query: gql`
            query getDepartments($input: GetDepartmentsInput!) {
              getDepartments(input: $input) {
                items {
                  id
                  name
                  institution {
                    id
                    name
                  }
                  users {
                    name
                    roles {
                      departmentId
                      role
                    }
                  }
                  createdAt
                }
                pager {
                  limit
                  offset
                  count
                }
              }
            }
          `,
          variables: { input },
        });

        if (!payload?.freezeCache) {
          commit('SET_DEPARTMENTS_LIST', response.data.getDepartments.items);
          commit('SET_DEPARTMENTS_PAGEINFO', response.data.getDepartments.pager);
          commit('SET_REFRESH_DEPARTMENTS', false);
        }

        return response.data.getDepartments;
      } catch (e) {
        console.error('ERROR', e.message);
        preventError(e);
      }
    },

    // === CREATE_DEPARTMENT ===
    async CREATE_DEPARTMENT({ rootGetters }, payload) {
      const input = {
        institutionId: rootGetters.activeInstitutionId,
        name: payload.name,
      };

      try {
        const response = await client.mutate({
          mutation: gql`
            mutation createDepartment($input: CreateDepartmentInput!) {
              createDepartment(input: $input) {
                department {
                  name
                }
                status
              }
            }
          `,
          variables: { input },
        });

        return response.data.createDepartment;
      } catch (e) {
        console.error('ERROR', e.message);
        preventError(e);
      }
    },

    // === UPDATE_DEPARTMENT ===
    async UPDATE_DEPARTMENT(_, payload) {
      try {
        const response = await client.mutate({
          mutation: gql`
            mutation updateDepartment($input: UpdateDepartmentInput!) {
              updateDepartment(input: $input) {
                department {
                  name
                }
                status
              }
            }
          `,
          variables: { input: payload },
        });

        return response.data.updateDepartment;
      } catch (e) {
        console.error('ERROR', e.message);
        preventError(e);
      }
    },
  },
};
